import { objectKeys } from '@scalar/helpers/object/object-keys'
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref'
import { resolve } from '@scalar/workspace-store/resolve'
import type { SchemaObject } from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import { isArraySchema } from '@scalar/workspace-store/schemas/v3.1/strict/type-guards'

/**
 * Schema keywords whose value should reflect the *last* occurrence when merging
 * `allOf` members. Most keywords keep the first occurrence, but for human-facing
 * annotations a later subschema is expected to override an earlier one — matching
 * OpenAPI/JSON Schema tooling like Swagger UI.
 */
const LAST_WINS_KEYS = new Set<string>(['description', 'title'])

/**
 * Merges multiple OpenAPI schema objects into a single schema object.
 * Handles nested allOf compositions and merges properties recursively.
 *
 * @param schemas - Array of OpenAPI schema objects to merge
 * @param rootSchema - Optional root schema to merge with the result
 * @param seenRefs - `$ref` strings already being merged higher in the call stack
 * @returns Merged schema object
 */
export const mergeAllOfSchemas = (
  schemas: SchemaObject | undefined,
  rootSchema?: SchemaObject,
  seenRefs: Set<string> = new Set(),
): SchemaObject => {
  // Handle max depth, empty or invalid input
  if (!schemas?.allOf?.length || !Array.isArray(schemas.allOf)) {
    return rootSchema || ({} as SchemaObject)
  }

  // Initialize result object once
  const result = {} as SchemaObject

  // Extract base schema properties (everything except allOf)
  const { allOf: _, ...baseSchema } = schemas

  // Process allOf schemas first
  for (const _schema of schemas.allOf) {
    if (!_schema || typeof _schema !== 'object') {
      continue
    }

    // Resolve ref if present
    const schema = resolve.schema(_schema)

    // Handle nested allOf recursively
    if (schema.allOf) {
      const nestedMerged = mergeAllOfSchemas(schema, undefined, seenRefs)
      mergeSchemaIntoResult(result, nestedMerged, false, seenRefs)
      continue
    }

    mergeSchemaIntoResult(result, schema, false, seenRefs)
  }

  // Apply base schema properties with precedence over allOf properties
  if (Object.keys(baseSchema).length > 0) {
    mergeSchemaIntoResult(result, baseSchema, true, seenRefs)
  }

  // Process root schema last if provided
  if (rootSchema && typeof rootSchema === 'object') {
    if (rootSchema.allOf) {
      const nestedMerged = mergeAllOfSchemas(rootSchema, undefined, seenRefs)
      mergeSchemaIntoResult(result, nestedMerged, true, seenRefs)
    } else {
      mergeSchemaIntoResult(result, rootSchema, true, seenRefs)
    }
  }

  return result
}

/**
 * Efficiently merges a source schema into a target result object.
 * Handles all schema merging logic in a single optimized function.
 *
 * @param result - The target schema object to merge into
 * @param schema - The source schema object to merge from
 * @param override - Whether to override existing properties (default: false)
 */
const mergeSchemaIntoResult = (
  result: SchemaObject,
  schema: SchemaObject,
  override: boolean = false,
  seenRefs: Set<string> = new Set(),
): void => {
  // Early return if schema is empty
  const schemaKeys = objectKeys(schema)
  if (schemaKeys.length === 0) {
    return
  }

  // Loop through all schema properties and handle them appropriately
  for (const key of schemaKeys) {
    const value = getResolvedRef(schema[key]) as any

    if (value === undefined) {
      continue
    }

    // Required
    if ((key as string) === 'required') {
      // Merge required fields with deduplication
      if (Array.isArray(value) && value.length > 0) {
        // @ts-expect-error
        if (result.required?.length) {
          // @ts-expect-error
          result.required = [...new Set([...result.required, ...value])]
        } else {
          // @ts-expect-error
          result.required = value.slice()
        }
      }
    }
    // Properties
    else if ((key as string) === 'properties') {
      // Merge properties recursively
      if (value && typeof value === 'object') {
        // @ts-expect-error
        if (!result.properties) {
          // @ts-expect-error
          result.properties = {}
        }

        // @ts-expect-error
        mergePropertiesIntoResult(result.properties, value, seenRefs)
      }
    }
    // Items
    else if ((key as string) === 'items') {
      // Handle items (for both arrays and objects with items)
      const items = resolve.schema(value)
      if (items) {
        if (isArraySchema(schema)) {
          // @ts-expect-error
          if (!result.items) {
            // @ts-expect-error
            result.items = {}
          }

          // Handle allOf within array items
          if (items.allOf) {
            const mergedItems = mergeAllOfSchemas(items, undefined, seenRefs)
            // @ts-expect-error
            Object.assign(result.items, mergedItems)
          } else {
            // @ts-expect-error
            mergeItemsIntoResult(getResolvedRef(result.items), items, seenRefs)
          }
        }
        // For non-array types with items.allOf, merge into properties
        else if (items.allOf) {
          const mergedItems = mergeAllOfSchemas(items, undefined, seenRefs)
          if ('properties' in mergedItems) {
            if (!('properties' in result)) {
              // @ts-expect-error
              result.properties = {}
            }

            'properties' in result && mergePropertiesIntoResult(result.properties, mergedItems.properties, seenRefs)
          }
        }
        // For non-array types without allOf, still set items if not already set
        else if (!('items' in result)) {
          // @ts-expect-error
          result.items = items
        }
      }
    }
    // Enum
    else if (key === 'enum') {
      if (Array.isArray(value) && value.length > 0) {
        result.enum = [...new Set([...(result.enum || []), ...value])]
      }
    }
    // OneOf/AnyOf
    else if (key === 'oneOf' || key === 'anyOf') {
      // Preserve the composition itself so its variants keep rendering as a
      // selector. Flattening the option properties into the parent would drop
      // the variant structure entirely and silently lose branches that have no
      // top-level `properties` (for example branches that are themselves an
      // `allOf`). The sibling `allOf` members stay on `result` as base
      // properties, which the Schema component renders above the selector.
      if (Array.isArray(value) && value.length > 0 && (override || result[key] === undefined)) {
        result[key] = value
      }
    }
    // Skip allOf as it's handled at a higher level
    else if (key === 'allOf') {
      continue
    }
    // For all other properties, preserve the first occurrence or override if specified.
    // Annotation keywords (see LAST_WINS_KEYS) always take the latest value so a later
    // allOf member can override an earlier one.
    else {
      if (override || LAST_WINS_KEYS.has(key as string) || result[key] === undefined) {
        result[key] = value
      }
    }
  }
}

/**
 * Efficiently merges properties into a result object without creating new objects.
 */
const mergePropertiesIntoResult = (
  result: Extract<SchemaObject, { type: 'object' }>['properties'],
  properties: Extract<SchemaObject, { type: 'object' }>['properties'],
  seenRefs: Set<string> = new Set(),
): void => {
  const propertyKeys = Object.keys(properties ?? {})
  if (!properties || !result || propertyKeys.length === 0) {
    return
  }

  for (const key of propertyKeys) {
    const schema = resolve.schema(properties[key])
    if (!schema) {
      delete result[key]
      continue
    }

    if (typeof schema !== 'object') {
      result[key] = schema
      continue
    }

    if (!result[key]) {
      // Break self-referential cycles reached through `allOf`. When a brand-new
      // property's `$ref` is already being merged higher in the call stack,
      // keep the raw reference instead of recursing into its `allOf` forever.
      // This mirrors the guard in the "merge existing property" branch below and
      // covers schemas that point back at themselves through an `allOf` member
      // (e.g. a node whose `self` property $refs the node, which is an allOf).
      const rawProperty = properties[key]
      const newSchemaRef = (rawProperty as { $ref?: string })?.$ref
      if (rawProperty && typeof newSchemaRef === 'string' && seenRefs.has(newSchemaRef)) {
        result[key] = rawProperty
        continue
      }
      const nextNewSeenRefs = typeof newSchemaRef === 'string' ? new Set(seenRefs).add(newSchemaRef) : seenRefs

      // Handle new property with allOf
      if (schema.allOf) {
        result[key] = mergeAllOfSchemas(schema, undefined, nextNewSeenRefs)
      } else if (isArraySchema(schema) && resolve.schema(schema.items)?.allOf) {
        result[key] = {
          ...schema,
          items: mergeAllOfSchemas(resolve.schema(schema.items), undefined, seenRefs),
        }
      } else if (properties[key]) {
        result[key] = properties[key]
      }
      continue
    }

    // Merge existing property
    const existing = resolve.schema(result[key])

    // Break self-referential cycles: when this property's `$ref` is already
    // being merged higher in the call stack, keep the partially merged existing
    // schema instead of recursing into it again. This mirrors the guard in
    // `mergeItems` and covers schemas that point back at themselves through a
    // plain object property (e.g. a tree node whose `parent` $refs the node).
    const schemaRef = (schema as { $ref?: string }).$ref
    if (typeof schemaRef === 'string' && seenRefs.has(schemaRef)) {
      result[key] = existing
      continue
    }
    const nextSeenRefs = typeof schemaRef === 'string' ? new Set(seenRefs).add(schemaRef) : seenRefs

    if (schema.allOf) {
      result[key] = mergeAllOfSchemas({ allOf: [existing, ...schema.allOf] } as SchemaObject, undefined, nextSeenRefs)
    } else if (isArraySchema(schema) && isArraySchema(existing) && schema.items) {
      const existingItems = resolve.schema(existing.items)
      result[key] = {
        ...existing,
        type: 'array',
        items: existingItems
          ? mergeItems(existingItems, resolve.schema(schema.items), nextSeenRefs)
          : resolve.schema(schema.items),
      }
    } else {
      // Create merged object with properties handled separately
      if ('properties' in existing && 'properties' in schema) {
        const merged = { ...existing, ...schema }
        merged.properties = { ...existing.properties }
        mergePropertiesIntoResult(merged.properties, schema.properties, nextSeenRefs)
        result[key] = merged
      }
      // Simple merge without property recursion
      else {
        result[key] = { ...schema, ...existing }
      }
    }
  }
}

/**
 * Efficiently merges array items into a result object.
 */
const mergeItemsIntoResult = (result: SchemaObject, items: SchemaObject, seenRefs: Set<string> = new Set()): void => {
  // Handle allOf in items
  if (items.allOf || result.allOf) {
    // Build array without spreads for better performance
    const allOfSchemas: SchemaObject[] = []

    if (result.allOf) {
      for (const schema of result.allOf) {
        allOfSchemas.push(resolve.schema(schema))
      }
    } else {
      allOfSchemas.push(result)
    }

    if (items.allOf) {
      for (const schema of items.allOf) {
        allOfSchemas.push(resolve.schema(schema))
      }
    } else {
      allOfSchemas.push(items)
    }

    const merged = mergeAllOfSchemas({ allOf: allOfSchemas } as SchemaObject, undefined, seenRefs)
    Object.assign(result, merged)
    return
  }

  // Regular merge
  Object.assign(result, items)

  // Merge properties if both have them
  if ('properties' in result && 'properties' in items) {
    mergePropertiesIntoResult(result.properties, items.properties, seenRefs)
  }
}

/**
 * Helper function for merging items that returns a new object.
 */
const mergeItems = (
  existing: SchemaObject,
  incoming: SchemaObject,
  seenRefs: Set<string> = new Set(),
): SchemaObject => {
  const incomingRef = (incoming as { $ref?: string }).$ref

  if (typeof incomingRef === 'string') {
    if (seenRefs.has(incomingRef)) {
      return existing
    }
    return mergeItemsInner(existing, incoming, new Set(seenRefs).add(incomingRef))
  }

  return mergeItemsInner(existing, incoming, seenRefs)
}

const mergeItemsInner = (
  existing: SchemaObject,
  incoming: SchemaObject,
  seenRefs: Set<string> = new Set(),
): SchemaObject => {
  // Handle allOf in either schema
  if (existing.allOf || incoming.allOf) {
    // Build array without spreads for better performance
    const allOfSchemas: SchemaObject[] = []

    if (existing.allOf) {
      for (const schema of existing.allOf) {
        allOfSchemas.push(resolve.schema(schema))
      }
    } else {
      allOfSchemas.push(existing)
    }

    if (incoming.allOf) {
      for (const schema of incoming.allOf) {
        allOfSchemas.push(resolve.schema(schema))
      }
    } else {
      allOfSchemas.push(incoming)
    }

    return mergeAllOfSchemas({ allOf: allOfSchemas } as SchemaObject, undefined, seenRefs)
  }

  const merged = { ...existing, ...incoming }

  // Recursively merge properties if both have properties
  if ('properties' in existing && 'properties' in incoming) {
    // @ts-expect-error
    merged.properties = { ...existing.properties }
    // @ts-expect-error
    mergePropertiesIntoResult(merged.properties, incoming.properties, seenRefs)
  }

  return merged as SchemaObject
}
